import type { RaRecord } from "react-admin";

import type { RelayItsSource } from "./RelayItsSourceChip";

export type RelayAccessItsdata = {
  ITS_ID?: string | null;
  Full_Name?: string | null;
  Mobile?: string | null;
  WhatsApp_No?: string | null;
  Email?: string | null;
};

export type RelayAccessSabilData = {
  itsNo: string;
  name?: string | null;
  sabilNo?: string | null;
  sabilType?: string;
  address?: string | null;
  mohallah?: string | null;
};

/** Preferred contact for access requests: WhatsApp first, then directory mobile. */
export const resolveItsPreloadContactMobile = (itsdata?: RelayAccessItsdata | null): string => {
  const whatsapp = itsdata?.WhatsApp_No?.trim() || "";
  if (whatsapp) {
    return whatsapp;
  }
  return itsdata?.Mobile?.trim() || "";
};

export const getItsContactPhones = (itsdata?: RelayAccessItsdata | null) => {
  const mobile = itsdata?.Mobile?.trim() || "";
  const whatsapp = itsdata?.WhatsApp_No?.trim() || "";
  const phones: { label: string; value: string }[] = [];

  if (mobile) {
    phones.push({ label: "Mobile", value: mobile });
  }
  if (whatsapp && whatsapp !== mobile) {
    phones.push({ label: "WhatsApp", value: whatsapp });
  }

  return phones;
};

export const getAccessRequestItsSource = (record: RaRecord): RelayItsSource => {
  if (record.itsSource) {
    return record.itsSource as RelayItsSource;
  }
  if (record.itsdata) {
    return "directory";
  }
  if (record.sabilData) {
    return "sabil_only";
  }
  return "nowhere";
};

export const getAccessRequestSabilData = (record: RaRecord): RelayAccessSabilData | null => {
  const sabilData = record.sabilData as RelayAccessSabilData | undefined;
  return sabilData ?? null;
};

export const getAccessRequestDisplayName = (record: RaRecord): string => {
  const itsdata = record.itsdata as RelayAccessItsdata | undefined;
  const directoryName = itsdata?.Full_Name?.trim();
  if (directoryName) {
    return directoryName;
  }

  const sabilName = getAccessRequestSabilData(record)?.name?.trim();
  if (sabilName) {
    return sabilName;
  }

  const contactName =
    typeof record.contactNameSnapshot === "string" ? record.contactNameSnapshot.trim() : "";
  if (contactName) {
    return contactName;
  }

  return "—";
};

export const getAccessRequestMobile = (record: RaRecord) => {
  const contactMobile =
    typeof record.contactMobileSnapshot === "string" ? record.contactMobileSnapshot.trim() : "";
  if (contactMobile) {
    return contactMobile;
  }

  const itsdata = record.itsdata as RelayAccessItsdata | undefined;
  return resolveItsPreloadContactMobile(itsdata) || null;
};

export const formatAccessRequestMobile = (record: RaRecord) =>
  getAccessRequestMobile(record) ?? "—";

export const formatRelayAccessAddressValue = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as { address?: unknown };
      if (parsed && typeof parsed === "object" && "address" in parsed) {
        return typeof parsed.address === "string" ? parsed.address.trim() : "";
      }
    } catch {
      // fall through
    }
  }

  return trimmed;
};

export function resolveRelayItsSourceFromLookups(
  hasDirectory: boolean,
  hasSabil: boolean
): RelayItsSource {
  if (hasDirectory) {
    return "directory";
  }
  if (hasSabil) {
    return "sabil_only";
  }
  return "nowhere";
}

export { normalizeWhatsAppPhoneDigits, toWhatsAppChatHref } from "@/utils/whatsapp-chat-url";
